import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { FlipCard } from './FlipCard';
import { CreateActivityModal } from './CreateActivityModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
// import { Search, Filter, Plus, Activity } from 'lucide-react';
import { Search, Plus } from 'lucide-react';
// import { ActivityType } from '../types/activity';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { Activity, ActivityFormData, ActivityWithApplication } from '../types/activity';
import { CreatorProfileModal } from './CreatorProfileModal';
import { ApplicationStatus } from '../types/application';



export function ExploreActivities() {
    const navigate = useNavigate();
    const [activities, setActivities] = useState<ActivityWithApplication[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedDifficulty, setSelectedDifficulty] = useState('all');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState<string | null>(null);
    const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const DEFAULT_CENTER = { lat: 34.05968922154666, lng: -118.41856388696318 }; // West LA Haidilao
    const { user } = useAuth();

    const fetchActivities = async () => {
        const activities: Activity[] = await apiService.getActivitiesExcludingUserJoined({
            userId: user!.id,
            userLocation: userLocation || DEFAULT_CENTER
        });
        const activitiesWithCreatorApplication = activities.map(async (activity) => {
            // application(if exists) is unique given userId and activityId
            const application = await apiService.getApplicationByUserIdActivityId(user!.id, activity.id);
            const creator = await apiService.getUserById(activity.creatorId || '');
            return { ...activity, creator: creator, application: application } as ActivityWithApplication

        })
        setActivities(await Promise.all(activitiesWithCreatorApplication));
    }

    // Handle finding user location
    const handleLocateUser = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setUserLocation(newLocation);
                },
                (error) => {
                    // Fallback to default center
                    const fallbackLocation = { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng };
                    setUserLocation(fallbackLocation);
                },
                {
                    timeout: 10000,
                    enableHighAccuracy: false,
                    maximumAge: 300000 // 5 minutes
                }
            );
        } else {
            // Silently fallback when geolocation is not supported
            setIsLoadingLocation(false);
            // Fallback to default center
            const fallbackLocation = { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng };
            setUserLocation(fallbackLocation);
        }
    };

    useEffect(() => {
        handleLocateUser();
    }, []);

    // Fetch activities when user location changes
    useEffect(() => {
        if (userLocation) {
            fetchActivities();
        }
    }, [userLocation]);

    // Listen for the custom event from navigation
    useEffect(() => {
        const handleOpenCreateModal = () => {
            setIsCreateModalOpen(true);
        };

        window.addEventListener('open-create-modal', handleOpenCreateModal);

        return () => {
            window.removeEventListener('open-create-modal', handleOpenCreateModal);
        };
    }, []);

    const handleCreatorClick = (creatorId: string) => {
        setSelectedCreator(creatorId);
        setIsCreatorModalOpen(true);
    };

    const handleCreatorModalClose = () => {
        setIsCreatorModalOpen(false);
        setSelectedCreator(null);
    };

    const handleJoinActivity = (activityId: string) => {
        // need to decide if we want to do this
        // probably don't disappear the card immediately
        // so we do nothing right after join button is clicked
        // setActivities(activities.filter(activity => activity.id !== activityId));
    };

    const handleActivityCreated = (newActivity: ActivityFormData) => {
        setActivities(prev => [newActivity as ActivityWithApplication, ...prev]);
        toast.success('Activity created successfully');
    };

    // TODO: handle activity created show new activity in the list
    // const handleActivityCreated = (newActivity: any) => {
    //     setActivities(prev => [newActivity, ...prev]);
    //     // Show success message or notification here if desired
    // };

    const handleFollowChange = (creatorName: string, following: boolean) => {
        // TODO: handle follow change
    };

    const filteredActivities = activities.filter(activity => {
        const matchesSearch = activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.location?.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = selectedType === 'all' || activity.type === selectedType;
        const matchesDifficulty = selectedDifficulty === 'all' || activity.difficulty === selectedDifficulty;

        // we could display rejected or withdrawn applications for user to
        // potentially re-apply, but the activites that user already applied(pending)
        // or approved will not be shown
        const noApplication = Object.keys(activity.application || {}).length === 0;
        const matchesApplicationStatus = [
            // any terminal states could be potentially reapplied by user
            // the approved case also valid because the activities are the ones that user not joined
            // including the already quitted ones
            ApplicationStatus.REJECTED,
            ApplicationStatus.WITHDRAWN,
            ApplicationStatus.APPROVED,
        ].includes(activity.application?.status as ApplicationStatus);

        return matchesSearch
            && matchesType
            && matchesDifficulty
            && (noApplication || matchesApplicationStatus);
    });

    const activityTypes = [...new Set(activities.map(a => a.type))];


    // Format activity type for display (SPORTS_FITNESS -> Sports Fitness)
    const formatActivityType = (type: string) => {
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative page-transition">
            {/* Header section */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                    Explore Activities
                </h1>
                <p className="text-muted-foreground">
                    Discover exciting activities in your area. Click on any card to see more details!
                </p>
            </div>

            {/* Search and Filters - Glass effect container */}
            <div className="mb-8 p-4 rounded-xl glass">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                        <Input
                            placeholder="Search activities..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 bg-background/50 border-border/50 focus:bg-background transition-colors"
                        />
                    </div>

                    <Select value={selectedType} onValueChange={setSelectedType}>
                        <SelectTrigger className="w-full sm:w-48 bg-background/50 border-border/50">
                            <SelectValue placeholder="Activity Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {activityTypes.map(type => (
                                <SelectItem key={type} value={type}>{formatActivityType(type)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                        <SelectTrigger className="w-full sm:w-48 bg-background/50 border-border/50">
                            <SelectValue placeholder="Difficulty" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Levels</SelectItem>
                            <SelectItem value="Beginner">Beginner</SelectItem>
                            <SelectItem value="Intermediate">Intermediate</SelectItem>
                            <SelectItem value="Advanced">Advanced</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Activity Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredActivities.map(activity => (
                    <FlipCard
                        key={activity.id}
                        activity={activity}
                        onCreatorClick={handleCreatorClick}
                        onJoinActivity={handleJoinActivity}
                    />
                ))}
            </div>

            {filteredActivities.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground">No activities found matching your criteria.</p>
                </div>
            )}

            {/* Floating Action Button */}
            <TooltipProvider>
                <div className="fixed bottom-6 right-4 sm:right-6 z-50">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={() => navigate('/create-activity')}
                                size="lg"
                                className="relative rounded-full w-14 h-14 sm:w-16 sm:h-16 fab-shadow transition-all duration-300 hover:scale-105 bg-primary hover:bg-primary/90 group border-0"
                            >
                                {/* Pulse animation ring */}
                                <div className="absolute inset-0 rounded-full bg-primary/20 animate-pulse group-hover:animate-none"></div>
                                <Plus size={20} className="sm:w-6 sm:h-6 relative z-10" />
                                <span className="sr-only">Create new activity</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="hidden sm:block">
                            <p>Create new activity</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </TooltipProvider>

            {/* Floating Action Button */}
            {/* <div className="fixed bottom-6 right-6 z-50">
                <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    size="lg"
                    className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow"
                >
                    <Plus size={24} />
                    <span className="sr-only">Create new activity</span>
                </Button>
            </div> */}

            {/* Create Activity Modal */}
            {/* <CreateActivityModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onActivityCreated={handleActivityCreated}
            /> */}

            {/* Organizer Profile Modal */}
            {selectedCreator && (
                <CreatorProfileModal
                    isOpen={isCreatorModalOpen}
                    onClose={handleCreatorModalClose}
                    creatorId={selectedCreator}
                    onFollowChange={handleFollowChange}
                />
            )}
        </div>
    );
}
